import { App, Button, Listy, Pagination, Tabs, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import { WebSocketEventTypeEnum } from '../../../enums/WebSocketEventTypeEnum';
import useFormatTimestamp from '../../../hooks/useFormatTimestamp';
import type { Batch } from '../../../interfaces/Batch';
import {
  FileJobStatus,
  type FileJob,
  type GetBatchFilesResponse,
} from '../../../interfaces/BatchApiService';
import type { WebSocketMessageDataAiEvent } from '../../../interfaces/WebSocketMessage';
import { BatchService } from '../../../services/batchApiService';
import { useAppStore } from '../../../store/store';
import { getApiErrorCode } from '../../../utils/apiErrorCode';
import { dateFromTicks } from '../../../utils/utils';
import FileIcon from '../../Common/FileIcon';
import ListyMetaRow from '../../Common/ListyMetaRow';
import { LISTY_ITEM_NO_HOVER } from '../../Common/listyItemClassNames';
import { Icon } from '../../Core/Icon';

const batchService = new BatchService();
const PAGE_SIZE = 20;

export type BatchCounts = {
  totalCount: number;
  pendingCount: number;
  processingCount: number;
  successCount: number;
  failedCount: number;
  canceledCount: number;
};

export interface BatchDetailFormProps {
  batchId: number;
  /** List/detail row used to seed tab badges before `/files` returns. */
  batch?: Partial<
    Pick<
      Batch,
      | 'totalFiles'
      | 'pendingFiles'
      | 'processingFiles'
      | 'processedFiles'
      | 'failedFiles'
      | 'canceledFiles'
    >
  >;
  /** Called after retry/cancel so the parent list can refresh. */
  onListRefresh?: () => void;
  /** Close detail modal (e.g. batch gone after BATCH_NOT_FOUND). */
  onClose?: () => void;
}

type StatusTabKey =
  | FileJobStatus.Pending
  | FileJobStatus.Processing
  | FileJobStatus.Success
  | FileJobStatus.Failed
  | FileJobStatus.Canceled;

const ZERO_COUNTS: BatchCounts = {
  totalCount: 0,
  pendingCount: 0,
  processingCount: 0,
  successCount: 0,
  failedCount: 0,
  canceledCount: 0,
};

type BatchCountSeed = Partial<
  Pick<
    Batch,
    | 'totalFiles'
    | 'pendingFiles'
    | 'processingFiles'
    | 'processedFiles'
    | 'failedFiles'
    | 'canceledFiles'
  >
>;

/** Map batch-level REST / WS fields → tab badge counts. */
export function countsFromBatch(batch?: BatchCountSeed | null): BatchCounts {
  if (!batch) return { ...ZERO_COUNTS };
  return {
    totalCount: batch.totalFiles ?? 0,
    pendingCount: batch.pendingFiles ?? 0,
    processingCount: batch.processingFiles ?? 0,
    // REST/WS: processedFiles = succeeded jobs
    successCount: batch.processedFiles ?? 0,
    failedCount: batch.failedFiles ?? 0,
    canceledCount: batch.canceledFiles ?? 0,
  };
}

export function isAllZeroStatusCounts(
  counts: Pick<
    BatchCounts,
    'pendingCount' | 'processingCount' | 'successCount' | 'failedCount' | 'canceledCount'
  >,
): boolean {
  return (
    counts.pendingCount === 0 &&
    counts.processingCount === 0 &&
    counts.successCount === 0 &&
    counts.failedCount === 0 &&
    counts.canceledCount === 0
  );
}

function countKeyForStatus(
  status: StatusTabKey,
): keyof Pick<
  BatchCounts,
  'pendingCount' | 'processingCount' | 'successCount' | 'failedCount' | 'canceledCount'
> {
  switch (status) {
    case FileJobStatus.Processing:
      return 'processingCount';
    case FileJobStatus.Pending:
      return 'pendingCount';
    case FileJobStatus.Failed:
      return 'failedCount';
    case FileJobStatus.Success:
      return 'successCount';
    case FileJobStatus.Canceled:
      return 'canceledCount';
  }
}

/**
 * Map `/files` response → badge counts.
 * Live BE often omits `pendingCount` / `canceledCount` / `totalCount` while still
 * returning filtered `total` + files — fill the active tab from `total` in that case.
 */
export function countsFromFilesResponse(
  response: GetBatchFilesResponse,
  statusFilter: StatusTabKey,
): BatchCounts {
  const counts: BatchCounts = {
    totalCount: response.totalCount ?? 0,
    pendingCount: response.pendingCount ?? 0,
    processingCount: response.processingCount ?? 0,
    successCount: response.successCount ?? 0,
    failedCount: response.failedCount ?? 0,
    canceledCount: response.canceledCount ?? 0,
  };

  const filteredTotal = response.total ?? 0;
  if (filteredTotal > 0) {
    const key = countKeyForStatus(statusFilter);
    const raw = response[key];
    if (raw == null || isAllZeroStatusCounts(counts)) {
      counts[key] = filteredTotal;
    }
    if (!counts.totalCount) {
      counts.totalCount = filteredTotal;
    }
  }

  return counts;
}

/**
 * Prefer batch-level / seeded counts when filtered `/files` returns all-zero badges
 * (BE sometimes zeros non-matching status counts under statusFilter).
 * When `/files` only fills the active tab (via `total`), merge onto previous badges.
 */
export function mergeCountsPreferSeeded(seeded: BatchCounts, fromFiles: BatchCounts): BatchCounts {
  if (isAllZeroStatusCounts(fromFiles) && !isAllZeroStatusCounts(seeded)) {
    return seeded;
  }
  if (isAllZeroStatusCounts(fromFiles)) {
    return fromFiles;
  }
  // Keep prior non-zero tabs when response only updated one status (common with omitted *Count).
  if (!isAllZeroStatusCounts(seeded)) {
    return {
      totalCount: Math.max(fromFiles.totalCount, seeded.totalCount),
      pendingCount: fromFiles.pendingCount || seeded.pendingCount,
      processingCount: fromFiles.processingCount || seeded.processingCount,
      successCount: fromFiles.successCount || seeded.successCount,
      failedCount: fromFiles.failedCount || seeded.failedCount,
      canceledCount: fromFiles.canceledCount || seeded.canceledCount,
    };
  }
  return fromFiles;
}

function mapAiEventStatusToFileJobStatus(status?: string): FileJobStatus | undefined {
  switch (status?.trim().toLowerCase()) {
    case 'queued':
      return FileJobStatus.Pending;
    case 'classifying':
      return FileJobStatus.Processing;
    case 'done':
      return FileJobStatus.Success;
    case 'error':
      return FileJobStatus.Failed;
    case 'canceled':
      return FileJobStatus.Canceled;
    default:
      return undefined;
  }
}

export function resolveFileJobActionError(
  t: (key: string) => string,
  error: unknown,
  fallbackKey: string,
): string {
  const code = getApiErrorCode((error as { response?: unknown })?.response);
  switch (code) {
    case ApiErrorEnum.BatchNotFound:
      return t('errors.batchNotFound');
    case ApiErrorEnum.BatchNotCancellable:
      return t('errors.batchNotCancellable');
    case ApiErrorEnum.BatchRetryNotSupported:
      return t('errors.batchRetryNotSupported');
    case ApiErrorEnum.NoFileJobsFound:
      return t('errors.noFileJobsFound');
    case ApiErrorEnum.QueueUnavailable:
      return t('errors.queueUnavailable');
    default:
      return t(fallbackKey);
  }
}

/** Prefer first status tab with count > 0. */
export function pickDefaultTab(counts: {
  processingCount: number;
  pendingCount: number;
  failedCount: number;
  successCount: number;
  canceledCount: number;
}): StatusTabKey {
  if (counts.processingCount > 0) return FileJobStatus.Processing;
  if (counts.pendingCount > 0) return FileJobStatus.Pending;
  if (counts.failedCount > 0) return FileJobStatus.Failed;
  if (counts.successCount > 0) return FileJobStatus.Success;
  if (counts.canceledCount > 0) return FileJobStatus.Canceled;
  return FileJobStatus.Pending;
}

/**
 * Batch file-jobs detail body. Intended for `modal.confirm` (no `<Modal>` wrapper).
 */
export default function BatchDetailForm({
  batchId,
  batch,
  onListRefresh,
  onClose,
}: BatchDetailFormProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const formatTimestamp = useFormatTimestamp();

  const seededCounts = useMemo(() => countsFromBatch(batch), [batch]);
  const [activeTab, setActiveTab] = useState<StatusTabKey>(() => pickDefaultTab(seededCounts));
  const [page, setPage] = useState(1);
  const [files, setFiles] = useState<FileJob[]>([]);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [counts, setCounts] = useState<BatchCounts>(() => seededCounts);
  const [isLoading, setIsLoading] = useState(true);
  const [truncatedHint, setTruncatedHint] = useState(false);
  // Seeded non-zero counts already chose the tab — skip Pending-first /files guess.
  const hasChosenDefaultTabRef = useRef(!isAllZeroStatusCounts(seededCounts));

  const loadFiles = useCallback(
    async (
      statusFilter: StatusTabKey,
      pageIndex: number,
    ): Promise<GetBatchFilesResponse | null> => {
      setIsLoading(true);
      try {
        const response: GetBatchFilesResponse = await batchService.getBatchFiles(batchId, {
          offset: (pageIndex - 1) * PAGE_SIZE,
          count: PAGE_SIZE,
          statusFilter,
        });
        setFiles(response.files ?? []);
        setFilteredTotal(response.total ?? 0);
        const fromFiles = countsFromFilesResponse(response, statusFilter);
        setCounts((prev) => mergeCountsPreferSeeded(prev, fromFiles));
        return response;
      } catch (error) {
        console.error('Error fetching batch files:', error);
        message.error(resolveFileJobActionError(t, error, 'errors.genericError'));
        setFiles([]);
        setFilteredTotal(0);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [batchId, message, t],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const response = await loadFiles(activeTab, page);
      if (cancelled || !response || hasChosenDefaultTabRef.current) return;
      hasChosenDefaultTabRef.current = true;

      const tabCounts = countsFromFilesResponse(response, activeTab);
      let nextTab = pickDefaultTab(tabCounts);

      // Counts all-zero but batch has jobs: Pending page empty → try Failed (common terminal case).
      const allCountsZero = isAllZeroStatusCounts(tabCounts);
      const hasBatchJobs =
        (response.totalCount ?? 0) > 0 ||
        (response.total ?? 0) > 0 ||
        (response.files?.length ?? 0) > 0;
      if (
        allCountsZero &&
        hasBatchJobs &&
        (response.files?.length ?? 0) === 0 &&
        activeTab === FileJobStatus.Pending
      ) {
        nextTab = FileJobStatus.Failed;
      }

      if (nextTab !== activeTab) {
        setActiveTab(nextTab);
        setPage(1);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, page, loadFiles]);

  // Prefer GET /batch counters immediately (authoritative vs filtered /files).
  useEffect(() => {
    let cancelled = false;
    void batchService
      .getBatch(batchId)
      .then((detail) => {
        if (cancelled) return;
        setTruncatedHint(Boolean(detail.failedFilesListTruncated));
        const fromDetail = countsFromBatch(detail);
        if (isAllZeroStatusCounts(fromDetail) && (detail.totalFiles ?? 0) === 0) return;

        setCounts((prev) =>
          isAllZeroStatusCounts(fromDetail) ? prev : mergeCountsPreferSeeded(prev, fromDetail),
        );

        if (!hasChosenDefaultTabRef.current && !isAllZeroStatusCounts(fromDetail)) {
          hasChosenDefaultTabRef.current = true;
          const nextTab = pickDefaultTab(fromDetail);
          setActiveTab((current) => (current === nextTab ? current : nextTab));
          setPage(1);
        }
      })
      .catch(() => {
        if (!cancelled) setTruncatedHint(false);
      });
    return () => {
      cancelled = true;
    };
  }, [batchId]);

  // Live row updates from classification events (no refetch).
  useEffect(() => {
    const unsubProgress = useAppStore.getState().events.on.batchProgressUpdated((payload) => {
      if (payload.batchId !== batchId) return;

      setCounts((prev) => ({
        totalCount: payload.total ?? prev.totalCount,
        pendingCount: payload.pending ?? prev.pendingCount,
        processingCount: payload.processing ?? prev.processingCount,
        successCount: payload.succeeded ?? prev.successCount,
        failedCount: payload.failed ?? prev.failedCount,
        canceledCount: payload.canceled ?? prev.canceledCount,
      }));

      if (payload.fileId == null || !payload.fileStatus) return;
      const mappedStatus = mapAiEventStatusToFileJobStatus(payload.fileStatus);
      if (!mappedStatus) return;

      setFiles((prev) => {
        const idx = prev.findIndex((file) => file.fileId === payload.fileId);
        if (idx < 0) {
          // Row not on this page — leave until refresh/tab change.
          return prev;
        }
        const next = [...prev];
        const current = next[idx];
        if (!current) return prev;
        next[idx] = {
          ...current,
          status: mappedStatus,
          ...(payload.fileName ? { fileName: payload.fileName } : {}),
        };
        return next;
      });
    });

    const unsubWs = useAppStore.getState().events.on.webSocketEvent((event) => {
      if (event.type?.toLowerCase() !== WebSocketEventTypeEnum.AiEvent) return;
      const data = event.data as WebSocketMessageDataAiEvent;
      if (data.event_type?.toLowerCase() !== WebSocketEventTypeEnum.Tags) return;
      if (data.batch_id !== batchId) return;

      const mappedStatus = mapAiEventStatusToFileJobStatus(data.status);
      if (!mappedStatus) return;

      const ids: number[] = [];
      if (typeof data.file_id === 'number' && data.file_id > 0) {
        ids.push(data.file_id);
      }
      if (Array.isArray(data.file_ids)) {
        for (const id of data.file_ids) {
          if (typeof id === 'number' && id > 0) ids.push(id);
        }
      }
      if (ids.length === 0) return;

      const idSet = new Set(ids);
      setFiles((prev) =>
        prev.map((file) =>
          idSet.has(file.fileId)
            ? {
                ...file,
                status: mappedStatus,
                ...(typeof data.file_name === 'string' && ids.length === 1
                  ? { fileName: data.file_name }
                  : {}),
              }
            : file,
        ),
      );
    });

    return () => {
      unsubProgress();
      unsubWs();
    };
  }, [batchId]);

  const getFileExtension = (fileName: string): string => {
    const parts = fileName.split('.');
    return parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
  };

  const statusLabel = useCallback(
    (status: string): string => {
      const key = status.toLowerCase();
      return t(`modal.batchDetailForm.status.${key}`, {
        defaultValue: status,
      });
    },
    [t],
  );

  const handleRetryFile = async (file: FileJob): Promise<void> => {
    try {
      await batchService.retryFileJobs({
        batchId,
        fileIds: [file.fileId],
      });
      message.success(t('modal.batchDetailForm.fileRetried'));
      onListRefresh?.();
      await loadFiles(activeTab, page);
    } catch (error) {
      console.error('Error retrying file job:', error);
      message.error(resolveFileJobActionError(t, error, 'modal.batchDetailForm.fileRetryError'));
      const code = getApiErrorCode((error as { response?: unknown })?.response);
      if (code === ApiErrorEnum.BatchNotFound) {
        onListRefresh?.();
        onClose?.();
      } else if (code === ApiErrorEnum.NoFileJobsFound) {
        onListRefresh?.();
        await loadFiles(activeTab, page);
      }
    }
  };

  const handleCancelFile = async (file: FileJob): Promise<void> => {
    try {
      await batchService.cancelFileJobs({
        batchId,
        fileIds: [file.fileId],
      });
      message.success(t('modal.batchDetailForm.fileCancelled'));
      onListRefresh?.();
      await loadFiles(activeTab, page);
    } catch (error) {
      console.error('Error cancelling file job:', error);
      message.error(resolveFileJobActionError(t, error, 'modal.batchDetailForm.fileCancelError'));
      const code = getApiErrorCode((error as { response?: unknown })?.response);
      if (code === ApiErrorEnum.BatchNotFound) {
        onListRefresh?.();
        onClose?.();
      } else if (code === ApiErrorEnum.NoFileJobsFound) {
        // Stale detail — refresh jobs from server
        onListRefresh?.();
        await loadFiles(activeTab, page);
      }
    }
  };

  const tabItems = useMemo(
    () => [
      {
        key: FileJobStatus.Processing,
        label: (
          <span>
            {t('modal.batchDetailForm.tabs.processing')}{' '}
            <Tag className="ml-1">{counts.processingCount}</Tag>
          </span>
        ),
      },
      {
        key: FileJobStatus.Pending,
        label: (
          <span>
            {t('modal.batchDetailForm.tabs.pending')}{' '}
            <Tag className="ml-1">{counts.pendingCount}</Tag>
          </span>
        ),
      },
      {
        key: FileJobStatus.Failed,
        label: (
          <span>
            {t('modal.batchDetailForm.tabs.failed')}{' '}
            <Tag className="ml-1">{counts.failedCount}</Tag>
          </span>
        ),
      },
      {
        key: FileJobStatus.Success,
        label: (
          <span>
            {t('modal.batchDetailForm.tabs.success')}{' '}
            <Tag className="ml-1">{counts.successCount}</Tag>
          </span>
        ),
      },
      {
        key: FileJobStatus.Canceled,
        label: (
          <span>
            {t('modal.batchDetailForm.tabs.canceled')}{' '}
            <Tag className="ml-1">{counts.canceledCount}</Tag>
          </span>
        ),
      },
    ],
    [counts, t],
  );

  return (
    <div className="flex max-h-[70vh] flex-col gap-3">
      {truncatedHint && (
        <div className="text-gray-dark text-xs">{t('modal.batchDetailForm.truncatedHint')}</div>
      )}

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key as StatusTabKey);
          setPage(1);
        }}
        items={tabItems}
        size="small"
      />

      {isLoading && files.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <Icon name="LoaderIcon" className="text-blue-dark h-8 w-8 animate-spin" />
        </div>
      ) : files.length === 0 ? (
        <div className="text-gray-dark py-8 text-center">{t('modal.batchDetailForm.noFiles')}</div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Listy
            items={files}
            rowKey="fileId"
            classNames={{ item: clsx(LISTY_ITEM_NO_HOVER, 'px-0! py-3!') }}
            itemRender={(file: FileJob) => {
              const isFailed =
                file.status === FileJobStatus.Failed ||
                String(file.status).toLowerCase() === 'failed';
              return (
                <ListyMetaRow
                  className="py-0"
                  avatar={
                    <FileIcon
                      fileExtension={getFileExtension(file.fileName)}
                      isFolder={file.isFolder}
                      className="h-8 w-8"
                    />
                  }
                  title={
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-blue-dark text-sm font-medium">{file.fileName}</span>
                      <span className="text-gray-dark ml-2 shrink-0 text-xs">
                        {formatTimestamp(dateFromTicks(file.updatedAt))}
                      </span>
                    </div>
                  }
                  description={
                    <div className="space-y-1">
                      <div className="text-gray-dark flex flex-wrap items-center gap-2 text-xs">
                        <span>{statusLabel(String(file.status))}</span>
                        {file.attempt > 1 && (
                          <span>{t('modal.batchDetailForm.attempt', { count: file.attempt })}</span>
                        )}
                      </div>
                      {file.lastError ? (
                        <div className="text-red-dark text-xs">{file.lastError}</div>
                      ) : null}
                    </div>
                  }
                  actions={
                    isFailed ? (
                      <>
                        <Tooltip title={t('common.retry')}>
                          <Button
                            size="small"
                            type="text"
                            onClick={() => void handleRetryFile(file)}
                            icon={<Icon name="RotateCwIcon" className="h-4 w-4" />}
                          />
                        </Tooltip>
                        <Tooltip title={t('common.cancel')}>
                          <Button
                            size="small"
                            type="text"
                            danger
                            onClick={() => void handleCancelFile(file)}
                            icon={<Icon name="XIcon" className="h-4 w-4" />}
                          />
                        </Tooltip>
                      </>
                    ) : undefined
                  }
                />
              );
            }}
          />
        </div>
      )}

      {filteredTotal > PAGE_SIZE && (
        <div className="flex justify-end">
          <Pagination
            size="small"
            current={page}
            pageSize={PAGE_SIZE}
            total={filteredTotal}
            onChange={(nextPage) => setPage(nextPage)}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
}
